import { AxiosError } from 'axios';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { ProjectMilestone } from '@/types/types';

export type ProjectMilestoneError = {
  milestone_date?: string[];
};

const useProjectMilestones = (projectId: string | number, options?: { enabled?: boolean }) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.projectMilestones.list(projectId).queryKey,
    enabled: options?.enabled,
    queryFn: async () => {
      const { data } = await axiosInstance.get<ProjectMilestone[]>(
        `/api/v1/projects/${projectId}/project_milestones`
      );
      return data;
    },
  });
};

const useProjectMilestone = (projectId: string | number, milestoneId: string | number) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.projectMilestones.detail(projectId, milestoneId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<ProjectMilestone>(
        `/api/v1/projects/${projectId}/project_milestones/${milestoneId}`
      );
      return data;
    },
  });
};

const useCreateProjectMilestone = (projectId: string | number | number) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newMilestone: Partial<ProjectMilestone>) => {
      const { data } = await axiosInstance.post<ProjectMilestone>(
        `/api/v1/projects/${projectId}/project_milestones`,
        newMilestone
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectMilestones.list(projectId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.planningProjects._def,
      });
    },
    onError: (data: AxiosError<ProjectMilestoneError>) => {
      return data.response?.data;
    },
  });
};

const useUpdateProjectMilestone = (projectId: string | number, milestoneId: string | number) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      updatedMilestone,
      updateAll = false,
      difference = 0,
    }: {
      updatedMilestone: Partial<ProjectMilestone>;
      updateAll?: boolean;
      difference?: number;
    }) => {
      const { data } = await axiosInstance.put<ProjectMilestone>(
        `/api/v1/projects/${projectId}/project_milestones/${milestoneId}`,
        updatedMilestone,
        { params: { updateAll, difference } }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectMilestones.list(projectId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectMilestones.detail(projectId, milestoneId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.planningProjects._def,
      });
      if (variables.updateAll) {
        queryClient.invalidateQueries({ queryKey: queryKeys.intervals._def });
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks._def });
      }
    },
  });
};

const useDeleteProjectMilestone = (projectId: string | number) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (milestoneId: ProjectMilestone['id']) => {
      await axiosInstance.delete(`/api/v1/projects/${projectId}/project_milestones/${milestoneId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectMilestones.list(projectId).queryKey,
      });
    },
  });
};

const useCompleteProjectMilestone = (projectId: string | number) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      milestoneId,
      complete,
    }: {
      milestoneId: ProjectMilestone['id'];
      complete: boolean;
    }) => {
      const action = complete ? 'complete' : 'uncomplete';
      const { data } = await axiosInstance.patch<ProjectMilestone>(
        `/api/v1/projects/${projectId}/project_milestones/${milestoneId}/${action}`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectMilestones.list(projectId).queryKey,
      });
    },
  });
};

export {
  useProjectMilestones,
  useProjectMilestone,
  useCreateProjectMilestone,
  useUpdateProjectMilestone,
  useDeleteProjectMilestone,
  useCompleteProjectMilestone,
};
